import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Save, Table2 } from 'lucide-react';
import {
  SAP_MAPPABLE_FIELDS,
  SAP_PUSH_STEP_LABELS,
  type SapMapping,
  type SapMappingHealth,
  type SapPushStep,
} from '@hcc/types';
import { useCurrentUser } from '../../../hooks/useAuth';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { api, ApiError } from '../../../lib/api';
import { MappingRow } from './MappingRow';
import { EMPTY_DRAFT, type MappingDrafts, type SapFormSummary } from './mappingTypes';

/**
 * SAP field mapping editor: point each SAP target field at the form
 * field that captures it. Forms are HR-authored, so this is data, not
 * code — republished forms keep working as long as field keys survive,
 * and the health check flags anything that drifted.
 */
export function SapMappings() {
  const { data: user, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const isAdmin = !!user?.roles.includes('admin');

  const forms = useQuery({
    queryKey: ['sap-forms'],
    queryFn: () => api<{ forms: SapFormSummary[] }>('/api/admin/sap/forms'),
    enabled: isAdmin,
  });
  const saved = useQuery({
    queryKey: ['sap-mappings'],
    queryFn: () => api<{ mappings: SapMapping[] }>('/api/admin/sap/mappings'),
    enabled: isAdmin,
  });
  const health = useQuery({
    queryKey: ['sap-mapping-health'],
    queryFn: () => api<{ health: SapMappingHealth[] }>('/api/admin/sap/mappings/health'),
    enabled: isAdmin,
  });

  const [drafts, setDrafts] = useState<MappingDrafts>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!saved.data) return;
    const next: MappingDrafts = {};
    for (const m of saved.data.mappings) {
      next[m.sap_field] = {
        form_key: m.form_key,
        field_key: m.field_key,
        value_map: m.value_map ?? {},
      };
    }
    setDrafts(next);
    setDirty(false);
  }, [saved.data]);

  const healthByField = useMemo(() => {
    const map = new Map<string, SapMappingHealth>();
    for (const h of health.data?.health ?? []) map.set(h.sap_field, h);
    return map;
  }, [health.data]);

  const groups = useMemo(() => {
    const byStep = new Map<SapPushStep, typeof SAP_MAPPABLE_FIELDS>();
    for (const def of SAP_MAPPABLE_FIELDS) {
      byStep.set(def.step, [...(byStep.get(def.step) ?? []), def]);
    }
    return [...byStep.entries()];
  }, []);

  const save = useMutation({
    mutationFn: () =>
      api<{ saved: number }>('/api/admin/sap/mappings', {
        method: 'PUT',
        body: JSON.stringify({
          mappings: Object.entries(drafts).map(([sap_field, d]) => ({ sap_field, ...d })),
        }),
      }),
    onSuccess: (r) => {
      toast.success(`Saved ${r.saved} mappings`);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ['sap-mappings'] });
      void queryClient.invalidateQueries({ queryKey: ['sap-mapping-health'] });
      void queryClient.invalidateQueries({ queryKey: ['sap-status'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'save failed'),
  });

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!isAdmin) {
    return <main className="p-6 text-sm text-fg-muted">SAP mappings are restricted to admin.</main>;
  }

  const loading = forms.isLoading || saved.isLoading;
  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to="/admin/sap"
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4" />
          SAP integration
        </Link>

        <header className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
              <Table2 className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-accent">Admin</div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">SAP field mappings</h1>
              <p className="mt-1 text-sm text-fg-muted">
                Pick which onboarding form question feeds each SAP field. Dropdown answers also
                need a translation to the SAP code. Fields marked * are required before a person
                can be pushed.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="md"
            iconLeft={<Save className="h-4 w-4" />}
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save mappings
          </Button>
        </header>

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          groups.map(([step, defs]) => (
            <section
              key={step}
              // No overflow-hidden: it clips the field-picker popover on the
              // section's last rows. Nothing inside paints past the radius.
              className="rounded-lg border border-line/10 bg-surface shadow-sm"
            >
              <div className="border-b border-line/10 px-5 py-3">
                <h2 className="text-sm font-semibold">{SAP_PUSH_STEP_LABELS[step]}</h2>
              </div>
              <div className="divide-y divide-divider/10">
                {defs.map((def) => (
                  <MappingRow
                    key={def.id}
                    def={def}
                    draft={drafts[def.id] ?? EMPTY_DRAFT}
                    forms={forms.data?.forms ?? []}
                    health={healthByField.get(def.id)}
                    onChange={(next) => {
                      setDrafts((prev) => ({ ...prev, [def.id]: next }));
                      setDirty(true);
                    }}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </main>
  );
}
